import { ReactNode } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type SvgIcon from '@mui/material/SvgIcon';
import type { SxProps, Theme } from '@mui/material/styles';

type SvgIconComponent = typeof SvgIcon;

export type MetricStatColor = 'success' | 'violet' | 'error' | 'warning';

export interface MetricStatProps {
  icon: SvgIconComponent;
  label: ReactNode;
  value: ReactNode;
  color: MetricStatColor;
  sx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
  /** Overrides the value's typography; the H3 default suits a card. */
  valueSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
}

/**
 * A tinted stat: icon + label beside (or above) a large value. Size and
 * orientation are left to the caller via `sx` / `headerSx` so this can sit in
 * a dashboard tile, a sidebar, or a full-width bar without baking in one layout.
 */
export default function MetricStat({
  icon: Icon,
  label,
  value,
  color,
  sx,
  headerSx,
  valueSx,
  labelSx,
}: MetricStatProps) {
  return (
    <Stack
      sx={{
        boxSizing: 'border-box',
        flexShrink: 0,
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: '16px',
        padding: '24px',
        borderRadius: '4px',
        bgcolor: (theme) => theme.palette.semantic.card[color],
        ...sx,
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          gap: 1,
          alignItems: 'flex-start',
          ...headerSx,
        }}
      >
        <Icon
          sx={{
            width: '24px',
            height: '24px',
            flexShrink: 0,
            color: (theme) => theme.palette.semantic.metricText[color],
          }}
        />
        <Typography
          variant="body1"
          sx={{
            color: (theme) => theme.palette.semantic.metricText[color],
            ...labelSx,
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 400,
          color: (theme) => theme.palette.semantic.metricText[color],
          ...valueSx,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
